import { TableRow } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Study } from '../../../../shared/api/hscan/hospital';
import { createClientsideSortableTableContext } from '../../common/components/SortableTable/ClientSideSortableTable';
import {
  PresentationalTableProps,
  TableInformation,
  createTableElementInformationContext,
} from '../../common/components/SortableTable/PresentationalTable';
import {
  BasicTable,
  LoadingIndicator,
} from '../../common/components/SortableTable/RenderTable';
import { HscanTableCell } from '../../common/components/SortableTable/Table';
import { useModalityLabel } from '../../hooks/modalityHook';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

import { MobileMedicalImageTable } from './MobileMedicalImageTable';
import { StatusMessage } from './StatusMessage';
import { mapStatusForRequest } from './utils';

export const { TableContext, TableInformationProvider } =
  createClientsideSortableTableContext<Study, 'studyInstanceUID'>();
export const { TableElementContext, TableElementInformationProvider } =
  createTableElementInformationContext<Study, 'studyInstanceUID'>(TableContext);

const EmptyIndicator = (
  props: TableInformation<Study, 'studyInstanceUID'> & { searched: boolean },
) => {
  const { cellOptions, searched } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.imageList.table',
  });
  return (
    <TableRow>
      <HscanTableCell colSpan={cellOptions.length + 1} align="center">
        {searched ? t('message.empty') : t('message.searchYet')}
      </HscanTableCell>
    </TableRow>
  );
};

interface MedicalImageTableProps
  extends Omit<
    PresentationalTableProps<Study, 'studyInstanceUID'>,
    'identifyKey'
  > {
  unselectAll: () => void;
  searched: boolean;
}
export const MedicalImageTable = (props: MedicalImageTableProps) => {
  const { searched, ...others } = props;
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.request.imageList.table',
  });
  const modalityLabel = useModalityLabel();

  const columnHelper = useMemo(() => createColumnHelper<Study>(), []);
  const cellOptions = useMemo(
    () => [
      columnHelper.accessor('date', {
        header: t('columns.date') as string,
        cell: ({ getValue }) => moment(getValue()).format('YYYY.MM.DD'),
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
          sortable: true,
        },
      }),
      columnHelper.accessor('modalities', {
        header: t('columns.modality') as string,
        cell: ({ getValue }) =>
          getValue()
            .map(modality => modalityLabel(modality))
            .join(', '),
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
          sortable: true,
        },
      }),
      columnHelper.accessor('studyDescription', {
        header: t('columns.bodypart') as string,
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
      columnHelper.accessor('status', {
        header: t('columns.status') as string,
        cell: ({ getValue }) => (
          <StatusMessage status={mapStatusForRequest(getValue())} />
        ),
        meta: {
          cellStyle: { align: 'center' },
          headerStyle: { align: 'center' },
        },
      }),
    ],
    [columnHelper, modalityLabel, t],
  );

  const windowSize = useContext(WindowSizeContext);

  return (
    <TableInformationProvider
      {...others}
      cellOptions={cellOptions}
      identifyKey="studyInstanceUID"
      initialOrderBy="date">
      {windowSize === WindowSize.MOBILE ? (
        <MobileMedicalImageTable searched={searched} />
      ) : (
        <BasicTable
          tableContext={TableContext}
          elementContext={TableElementContext}
          ElementProvider={TableElementInformationProvider}
          renderLoading={LoadingIndicator}
          renderEmpty={props => (
            <EmptyIndicator {...props} searched={searched} />
          )}
        />
      )}
    </TableInformationProvider>
  );
};
